import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, MessageSquareText } from "lucide-react";
import { SMS_HREF } from "@/lib/site";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { useBlogPost } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

const formatDate = (s) => {
    try {
        return new Date(s).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return s;
    }
};

export default function BlogPost() {
    const { slug } = useParams();
    const post = useBlogPost(slug);

    useSEO({
        title: post?.title,
        description: post?.excerpt,
        image: post?.cover,
        jsonLd: post
            ? {
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: post.title,
                  description: post.excerpt,
                  image: post.cover,
                  datePublished: post.date,
                  author: {
                      "@type": "Organization",
                      name: "Limit Break Performance",
                  },
              }
            : null,
    });

    if (!post) {
        return (
            <div className="lbp-container py-24 text-center">
                <h1 className="font-display font-black uppercase text-white text-3xl">
                    Post not found
                </h1>
                <Link to="/articles" className="mt-6 inline-flex items-center gap-2 text-red-500 hover:underline">
                    <ArrowLeft className="w-4 h-4" /> Back to all articles
                </Link>
            </div>
        );
    }

    return (
        <article data-testid="blog-post-page">
            <Breadcrumb section="Articles" />
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-6 pb-12 sm:pb-20">
                    <Link
                        to="/articles"
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4" /> All Articles
                    </Link>
                    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-red-500" />
                            {formatDate(post.date)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-red-500" />
                            {post.readTime}
                        </span>
                    </div>
                    <EditableText
                        path={`blog.${post.slug}.title`}
                        defaultValue={post.title}
                        as="h1"
                        className="mt-3 font-display font-black uppercase tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl max-w-3xl"
                    />
                </div>
            </section>

            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-12 sm:py-16 max-w-3xl">
                    <div className="aspect-[16/9] overflow-hidden rounded-sm border border-white/10">
                        <img
                            src={post.cover}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div className="mt-10 space-y-6 text-zinc-300 leading-relaxed text-base sm:text-lg">
                        {post.body.map((p, i) => (
                            <EditableText
                                key={i}
                                path={`blog.${post.slug}.body.${i}`}
                                defaultValue={p}
                                multiline
                                as="p"
                            />
                        ))}
                    </div>
                    <div className="mt-12 rounded-sm border border-red-600/30 bg-red-600/10 p-6 sm:p-8">
                        <EditableText
                            path="blog.post_cta.title"
                            defaultValue="Have a question about your build?"
                            as="h3"
                            className="font-display font-black uppercase tracking-tight text-white text-xl sm:text-2xl"
                        />
                        <EditableText
                            path="blog.post_cta.body"
                            defaultValue="Text me — I'll give you a straight answer, even if it's not what makes me money."
                            multiline
                            as="p"
                            className="mt-2 text-zinc-300"
                        />
                        <a
                            href={SMS_HREF}
                            data-testid="blog-post-text-button"
                            className="mt-5 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                        >
                            <MessageSquareText className="w-4 h-4" />
                            Text Me
                        </a>
                    </div>
                </div>
            </section>
        </article>
    );
}
